import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type GcpConnectionStatus = 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';

/** One connected GCP project (service-account secrets stripped). */
export interface GcpProject {
  projectId:         string;
  label?:            string;
  defaultRegion?:    string;
  enabledRegions:    string[];
  isPrimary?:        boolean;
  connectionStatus?: GcpConnectionStatus;
  healthStatus?:     string;
  lastTestedAt?:     string;
  lastSyncAt?:       string;
  failureReason?:    string;
  connectedAt?:      string;
}

export interface GcpConnection {
  provider:                 'GCP';
  connectionStatus:         GcpConnectionStatus;
  serviceAccountConfigured: boolean;
  clientEmail:              string | null;
  projects:                 GcpProject[];
  primaryProjectId:         string | null;
  projectCount:             number;
}

export interface GcpTestResult {
  success:          boolean;
  projectId:        string;
  connectionStatus: GcpConnectionStatus;
  verificationMode: 'stub' | 'live';
}

@Injectable({ providedIn: 'root' })
export class GcpService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/gcp`;

  constructor(private readonly http: HttpClient) {}

  /** The GCP integration state (projects + service-account status). */
  getConnection(): Observable<GcpConnection | null> {
    return this.http.get<GcpConnection>(`${this.base}/connection`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /** Connects (and validates) a GCP project via a service account key. */
  testConnection(input: {
    serviceAccountKey: object | string; projectId: string;
    displayName?: string; defaultRegion?: string; enabledRegions?: string[];
  }): Observable<GcpTestResult> {
    return this.http.post<GcpTestResult>(`${this.base}/test-connection`, input);
  }

  listProjects(): Observable<{ projects: GcpProject[]; primaryProjectId?: string; projectCount: number }> {
    return this.http.get<{ projects: GcpProject[]; primaryProjectId?: string; projectCount: number }>(`${this.base}/projects`);
  }

  deleteProject(projectId: string): Observable<{ message: string; projectId: string }> {
    return this.http.delete<{ message: string; projectId: string }>(`${this.base}/projects/${projectId}`);
  }

  updateProjectRegions(projectId: string, defaultRegion: string, enabledRegions: string[]): Observable<{ projectId: string; defaultRegion: string; enabledRegions: string[] }> {
    return this.http.patch<{ projectId: string; defaultRegion: string; enabledRegions: string[] }>(
      `${this.base}/projects/${projectId}/regions`, { defaultRegion, enabledRegions },
    );
  }

  /** Re-verifies a connected project using the stored service account (no key re-entry). */
  verifyProject(projectId: string): Observable<{ success: boolean; projectId: string; connectionStatus: GcpConnectionStatus; message?: string }> {
    return this.http.post<{ success: boolean; projectId: string; connectionStatus: GcpConnectionStatus; message?: string }>(
      `${this.base}/projects/${projectId}/verify`, {},
    );
  }

  /** Promotes an existing ACTIVE project to be the primary. */
  setPrimaryProject(projectId: string): Observable<{ projectId: string; primaryProjectId: string; changed: boolean }> {
    return this.http.post<{ projectId: string; primaryProjectId: string; changed: boolean }>(
      `${this.base}/projects/${projectId}/primary`, {},
    );
  }

  disconnect(): Observable<{ message: string; projectsRemoved: number }> {
    return this.http.delete<{ message: string; projectsRemoved: number }>(`${this.base}/connection`);
  }
}
